// Máquina de estado do tour "o que mudou / what's new", compartilhada entre as
// views que exibem o tour (Compliance, CRM, Consolidador...). Frontend puro:
// persistência via localStorage versionada por navegador (whatsnew-storage),
// zero fetch/API.
//
// Contrato: a view que consome o mixin DEVE expor um computed/data
// `whatsnewTourConfig` retornando { moduleKey, version, steps, targets }. O
// mixin deriva daí os passos/alvos e a chave de persistência. O template usa:
//   - `whatsNewSteps` / `whatsNewTargets` (para o <Tour v-for> ancorado)
//   - `currentTour` / `isTourActive` (índice do passo ativo e visibilidade do "?")
//   - `startTour` / `closeTour` / `nextTour` (navegação/encerramento)
//
// O auto-start dispara direto no 1º passo na 1ª visita naquele navegador (sem
// modal), após um pequeno atraso no mounted para o header teleportado e as
// seções da view montarem — o modo ancorado do Tour precisa dos alvos no DOM.

import {
  isWhatsnewSeen,
  setWhatsnewSeen,
} from '@modules/global/utils/whatsnew-storage'

// Atraso (ms) antes do auto-start: dá tempo do teleport/render assentarem para
// o modo ancorado do Tour encontrar os alvos via querySelector.
const AUTO_START_DELAY_MS = 300

export const whatsnewTourMixin = {
  data() {
    return {
      currentTour: null,
      autoStartTourTimer: null,
    }
  },

  computed: {
    isTourActive() {
      return typeof this.currentTour === 'number'
    },

    whatsNewSteps() {
      const config = this.whatsnewTourConfig || {}
      return config.steps || []
    },

    whatsNewTargets() {
      const config = this.whatsnewTourConfig || {}
      return config.targets || []
    },
  },

  mounted() {
    this.warnOnTourConfigMismatch()
    this.autoStartTourTimer = setTimeout(
      this.maybeAutoStartTour,
      AUTO_START_DELAY_MS
    )
  },

  beforeDestroy() {
    if (this.autoStartTourTimer) {
      clearTimeout(this.autoStartTourTimer)
      this.autoStartTourTimer = null
    }
    // Sair da tela com o tour aberto conta como dispensar: o usuário já viu o
    // anúncio e navegou. Sem isto o tour reabriria na próxima visita, que é
    // justamente a reclamação de "não para de aparecer".
    if (this.isTourActive) {
      this.markTourAsSeen()
    }
  },

  methods: {
    // Invariante: um alvo por passo. Divergência não trava (passos sem alvo caem
    // no card centralizado), mas é erro de configuração — avisa alto em dev para
    // não degradar silenciosamente.
    warnOnTourConfigMismatch() {
      const steps = this.whatsNewSteps.length
      const targets = this.whatsNewTargets.length
      if (steps !== targets) {
        // Identifica pelo componente, não pela chave: a chave de persistência é
        // compartilhada entre as views (PAGE_HEADER_WHATSNEW) e não diria qual
        // config está torta.
        const view = this.$options.name || 'view sem name'
        // eslint-disable-next-line no-console
        console.warn(
          `[whatsnew-tour] "${view}": ${steps} passo(s) x ${targets} alvo(s) — devem ser 1:1; passos sem alvo cairão no card centralizado.`
        )
      }
    },

    whatsnewTourKeys() {
      const config = this.whatsnewTourConfig || {}
      return { moduleKey: config.moduleKey, version: config.version }
    },

    markTourAsSeen() {
      const { moduleKey, version } = this.whatsnewTourKeys()
      setWhatsnewSeen(moduleKey, version)
    },

    maybeAutoStartTour() {
      // Sem passos configurados não há o que destacar — mantém o tour inativo.
      if (!this.whatsNewSteps.length) return
      const { moduleKey, version } = this.whatsnewTourKeys()
      if (isWhatsnewSeen(moduleKey, version)) {
        this.currentTour = null
        return
      }
      this.currentTour = 0
    },

    startTour() {
      // Reabertura manual pelo "?": sempre reinicia no passo 0, sem checar storage.
      this.currentTour = 0
    },

    closeTour() {
      this.currentTour = null
      this.markTourAsSeen()
    },

    nextTour() {
      const next = (this.currentTour || 0) + 1
      if (next >= this.whatsNewSteps.length) {
        this.closeTour()
        return
      }
      this.currentTour = next
    },
  },
}
